import React, {
    ReactElement,
    useMemo,
    useState,
    useRef,
    useEffect,
} from "react";
import Image from "next/image";
import styles from "./styles.module.scss";
import { TCategoryList } from "@page-builder/ts/generated";
import { useDomainContext } from "@hooks/useDomainContext";
import { gettingQueriesAsString } from "@lib/shared/routerQueriesToString";
import { useRouter } from "next/router";
import { replacePlaceholders } from "@lib/sharedUtils";

export default function CategoryList({
    props = {},
}: {
    props?: TCategoryList["props"];
}): ReactElement {
    const showCategoryGroups =
        props.categoryGroups?.groups && props.categoryGroups.groups.length > 0;
    const [searchValue, setSearchValue] = useState("");
    const categoryGroupsRef = useRef<HTMLDivElement>(null);
    const [showLeftShadow, setShowLeftShadow] = useState(false);
    const [showRightShadow, setShowRightShadow] = useState(false);
    const [shouldCenter, setShouldCenter] = useState(true);
    const { query } = useRouter();
    const {
        title,
        description,
        categoriesList,
        addLinkButton,
        buttonText,
        enableSearch,
        searchPlaceholder,
    } = props;
    const { domain } = useDomainContext();
    const domainCategories = domain?.categories;
    const queryString = gettingQueriesAsString(query, "start", [
        "genericRoute",
        "keyword",
        "replaceKeyword",
        "slug",
    ]);
    const { visitDetails, category } = useDomainContext();
    const [activeCategoryGroup, setActiveCategoryGroup] = useState(
        showCategoryGroups && props.categoryGroups?.groups
            ? props.categoryGroups?.groups[0]
            : null,
    );

    const handleScroll = () => {
        const container = categoryGroupsRef.current;
        if (!container) return;

        const { scrollLeft, scrollWidth, clientWidth } = container;

        // Check if content overflows
        const hasOverflow = scrollWidth > clientWidth;
        setShouldCenter(!hasOverflow);

        // Update shadow visibility
        setShowLeftShadow(scrollLeft > 0);
        setShowRightShadow(scrollLeft < scrollWidth - clientWidth - 1);
    };

    useEffect(() => {
        const container = categoryGroupsRef.current;
        if (!container) return;

        // Initial check - use timeout to ensure DOM has rendered
        const timeoutId = setTimeout(() => {
            handleScroll();
        }, 0);

        // Add scroll listener
        container.addEventListener("scroll", handleScroll);

        // Check on resize
        window.addEventListener("resize", handleScroll);

        return () => {
            clearTimeout(timeoutId);
            container.removeEventListener("scroll", handleScroll);
            window.removeEventListener("resize", handleScroll);
        };
    }, [props.categoryGroups?.groups]);

    const finalCategoriesList = useMemo(() => {
        const mappedCategories: {
            link: string;
            title: string;
            description: string;
            icon: string;
            buttonText: string;
        }[] = [];

        if (!categoriesList || categoriesList?.auto) {
            domainCategories?.forEach((category) => {
                mappedCategories.push({
                    link: `/${category.slugAlias ?? category.slug}`,
                    title: category.label ?? category.name ?? "",
                    description: "",
                    icon: category.icon?.fullPath ?? "",
                    buttonText: buttonText ?? "Get A Free Quote",
                });
            });
        } else {
            categoriesList.categories?.forEach((category) => {
                mappedCategories.push({
                    link: `${category.link ?? ""}`,
                    title: category.title ?? "",
                    description: category.description ?? "",
                    icon: category.image ?? "",
                    buttonText:
                        category.buttonText ?? buttonText ?? "Get A Free Quote",
                });
            });
        }

        const finalFilteredCategories = mappedCategories.filter((el) => {
            return el.title
                .toLowerCase()
                .includes(searchValue.trim().toLowerCase());
        });
        if (showCategoryGroups) {
            return finalFilteredCategories.filter((categoryEl) => {
                return activeCategoryGroup?.categories?.includes(
                    categoryEl?.title,
                );
            });
        }

        return finalFilteredCategories;
    }, [categoriesList, domainCategories, searchValue, activeCategoryGroup]);

    return (
        <div className={`${styles["category-list"]} `}>
            <div>
                {title && (
                    <div
                        className={`mb-6 ${styles.title}`}
                        dangerouslySetInnerHTML={{
                            __html: replacePlaceholders(title, {
                                region: visitDetails.region,
                                city: visitDetails?.city,
                                category: category?.label ?? category?.name,
                            }),
                        }}
                    />
                )}
                {description && (
                    <div
                        dangerouslySetInnerHTML={{
                            __html: replacePlaceholders(description, {
                                region: visitDetails.region,
                                city: visitDetails?.city,
                                category: category?.label ?? category?.name,
                            }),
                        }}
                        className={styles.description}
                    />
                )}
                {enableSearch && (
                    <div className="flex">
                        <input
                            type="text"
                            className={styles.search}
                            placeholder={searchPlaceholder || "Search..."}
                            onChange={(e) => setSearchValue(e.target.value)}
                        />
                    </div>
                )}
                {showCategoryGroups && (
                    <div
                        className={`${styles["categoryGroupsWrapper"]} ${
                            showLeftShadow ? styles["showLeftShadow"] : ""
                        } ${showRightShadow ? styles["showRightShadow"] : ""}`}
                    >
                        <div className={styles["shadowLeft"]} />
                        <div className={styles["shadowRight"]} />
                        <div
                            ref={categoryGroupsRef}
                            className={`${styles["categoryGroupsContainer"]} ${
                                shouldCenter ? styles["centered"] : ""
                            }`}
                        >
                            {props.categoryGroups?.groups?.map(
                                (group, index) => (
                                    <div
                                        key={index}
                                        className={`${
                                            styles["categoryGroup"]
                                        } ${
                                            activeCategoryGroup?.title ===
                                            group.title
                                                ? styles["activeCategoryGroup"]
                                                : ""
                                        } `}
                                        onClick={() => {
                                            setActiveCategoryGroup(group);
                                        }}
                                    >
                                        {group.title}
                                    </div>
                                ),
                            )}
                        </div>
                    </div>
                )}
            </div>

            {finalCategoriesList.length ? (
                <div className={styles["categories"]}>
                    {finalCategoriesList?.map((category, index) => (
                        <div key={index} className={styles.category}>
                            {category.icon && (
                                <div
                                    className={`flex ${styles["imageContainer"]}`}
                                >
                                    <div className="relative w-full">
                                        <Image
                                            src={category.icon}
                                            layout="fill"
                                            alt={category.title ?? ""}
                                        />
                                    </div>
                                </div>
                            )}
                            <h3
                                dangerouslySetInnerHTML={{
                                    __html: category.title ?? "",
                                }}
                            />
                            {category.description && (
                                <p className={styles.description}>
                                    {category.description}
                                </p>
                            )}

                            <a
                                href={`${category.link}?${queryString}`}
                                className={
                                    addLinkButton
                                        ? styles["with-button"]
                                        : styles["without-button"]
                                }
                            >
                                {category.buttonText}
                            </a>
                        </div>
                    ))}
                </div>
            ) : (
                <div className={styles["no-search-results-text"]}>
                    Sorry, No Results Found
                </div>
            )}
        </div>
    );
}
